import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Pagination,
  TimelineItem,
  WorkerMessageFilters,
  lifecycleAPI,
  lifecycleErrorMessage,
} from '@/services/lifecycleAPI';

const KINDS = ['message', 'event', 'legacy'];
const CHANNELS = ['sms', 'push', 'email'];
const JOURNEYS = ['J1', 'J2', 'J3', 'J4', 'J5', 'J6', 'J7', 'J8', 'J9', 'TX'];

const useMessagingTimeline = (workerId: number | string) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawKind = searchParams.get('kind') || '';
  const rawChannel = searchParams.get('channel') || '';
  const rawJourney = searchParams.get('journey') || '';
  const rawPage = parseInt(searchParams.get('page') || '1', 10);
  const kind = KINDS.includes(rawKind) ? rawKind : '';
  const channel = CHANNELS.includes(rawChannel) ? rawChannel : '';
  const journey = JOURNEYS.includes(rawJourney) ? rawJourney : '';
  const page = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;

  const [items, setItems] = useState<TimelineItem[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const latest = useRef(0);
  const lastKey = useRef('');

  useEffect(() => {
    const request = ++latest.current;
    const key = [workerId, kind, channel, journey, page].join('|');
    if (key !== lastKey.current) setLoading(true);
    lastKey.current = key;
    setError(null);
    lifecycleAPI
      .listWorkerMessages(workerId, {
        kind: kind as WorkerMessageFilters['kind'],
        channel: channel as WorkerMessageFilters['channel'],
        journey_key: journey,
        page,
        per_page: 25,
      })
      .then((result) => {
        if (request !== latest.current) return;
        setItems(result.data);
        setPagination(result.pagination);
        setLoading(false);
      })
      .catch((err) => {
        if (request !== latest.current) return;
        setError(lifecycleErrorMessage(err, 'Could not load the messages.'));
        setLoading(false);
      });
  }, [workerId, kind, channel, journey, page, tick]);

  const reload = useCallback(() => setTick((value) => value + 1), []);

  const retry = () => {
    setLoading(true);
    reload();
  };

  const setFilter = (key: 'kind' | 'channel' | 'journey', value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setSearchParams(next);
  };

  const setPage = (value: number) => {
    const next = new URLSearchParams(searchParams);
    if (value > 1) next.set('page', String(value));
    else next.delete('page');
    setSearchParams(next);
  };

  return {
    items,
    pagination,
    loading,
    error,
    kind,
    channel,
    journey,
    page,
    reload,
    retry,
    setKind: (value: string) => setFilter('kind', value),
    setChannel: (value: string) => setFilter('channel', value),
    setJourney: (value: string) => setFilter('journey', value),
    setPage,
  };
};

export default useMessagingTimeline;
